import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Validate that the script content matches expected PostHog patterns
const isValidPostHogScript = (content: string): boolean => {
  // Check for expected PostHog patterns
  const hasPostHogInit = /posthog\.init\s*\(/.test(content);
  const hasPostHogHost = /api_host\s*:\s*['"]https:\/\/[a-z]+\.i\.posthog\.com['"]/.test(content) ||
                         /api_host\s*:\s*['"]https:\/\/app\.posthog\.com['"]/.test(content) ||
                         /api_host\s*:\s*['"]https:\/\/eu\.i\.posthog\.com['"]/.test(content);
  const hasPostHogKey = /posthog\.init\s*\(\s*['"][a-zA-Z0-9_-]+['"]/.test(content);
  
  // Block dangerous patterns
  const hasDangerousPatterns = 
    /eval\s*\(/.test(content) ||
    /Function\s*\(/.test(content) ||
    /document\.cookie/.test(content) ||
    /localStorage\.getItem/.test(content) ||
    /sessionStorage\.getItem/.test(content) ||
    /XMLHttpRequest/.test(content) ||
    /fetch\s*\((?!.*posthog)/.test(content) ||
    /window\.location\s*=/.test(content);
  
  return (hasPostHogInit || hasPostHogKey) && !hasDangerousPatterns;
};

// Defer script execution to improve TTI and FID
const deferExecution = (callback: () => void) => {
  // Wait for page to be fully interactive before loading analytics
  const loadAfterInteractive = () => {
    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(callback, { timeout: 5000 });
    } else {
      setTimeout(callback, 4000);
    }
  };
  
  // Use longer delay to avoid blocking first input
  if (document.readyState === 'complete') {
    setTimeout(loadAfterInteractive, 1000);
  } else {
    window.addEventListener('load', () => setTimeout(loadAfterInteractive, 1000), { once: true });
  }
};

export const PostHogSnippet = () => {
  const [snippet, setSnippet] = useState<string>("");
  const [isExecuted, setIsExecuted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "get-public-settings",
        );
        if (error) return;
        if (data?.posthog_snippet) setSnippet(data.posthog_snippet);
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (!snippet || isExecuted) return;

    // Extract JavaScript code from script tags if they exist
    const scriptContent = snippet
      .replace(/<script[^>]*>/gi, '')
      .replace(/<\/script>/gi, '')
      .trim();

    if (!scriptContent) return;

    // Validate the script content before executing
    if (!isValidPostHogScript(scriptContent)) {
      console.warn("PostHog snippet failed validation - script not loaded for security reasons");
      return;
    }

    // Defer execution to avoid blocking initial page interactivity
    deferExecution(() => {
      try {
        const scriptElement = document.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.textContent = scriptContent;
        document.head.appendChild(scriptElement);
        setIsExecuted(true);
      } catch (error) {
        console.error("Error executing PostHog script:", error);
      }
    });
  }, [snippet, isExecuted]);

  return null;
};
